import type { ReactNode } from 'react';
import { ListFilter } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';

interface MobileFiltersTriggerProps {
  activeCount: number;
  onClick: () => void;
  className?: string;
}

/** Botão “Filtros” — só mobile (`sm:hidden`). Largura intrínseca; o grid do `toolbarExtra` estica via `[&_button]:w-full`. */
export function MobileFiltersTrigger({ activeCount, onClick, className }: MobileFiltersTriggerProps) {
  return (
    <Button
      type="button"
      variant="secondary"
      className={cn('relative h-10 shrink-0 gap-1.5 px-3 sm:hidden', className)}
      onClick={onClick}
      aria-label="Abrir filtros"
    >
      <ListFilter className="size-4" />
      Filtros
      {activeCount > 0 ? (
        <span className="absolute -right-1.5 -top-1.5 grid min-w-5 place-items-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-5 text-primary-foreground">
          {activeCount}
        </span>
      ) : null}
    </Button>
  );
}

interface MobileFiltersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeCount: number;
  onClear: () => void;
  title?: string;
  description?: string;
  children: ReactNode;
}

/** Dialog de filtros empilhados — padrão Leads / Campanhas (mobile). */
export function MobileFiltersDialog({
  open,
  onOpenChange,
  activeCount,
  onClear,
  title = 'Filtros',
  description = 'Ajuste os filtros da listagem.',
  children,
}: MobileFiltersDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-2">{children}</div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="ghost" onClick={onClear} disabled={activeCount === 0}>
            Limpar
          </Button>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Ver resultados
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
